import * as React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { EmptyState } from './EmptyState';

export interface DataColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  sortBy?: (row: T) => string | number | Date | null | undefined;
  className?: string;
  /** Hidden by default unless toggled on. */
  hiddenInitially?: boolean;
  /** Header alignment. */
  align?: 'start' | 'end' | 'center';
}

interface DataTableProps<T> {
  data: readonly T[];
  columns: readonly DataColumn<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: {
    message: string;
    recovery?: string;
    code?: string;
    status?: number;
    details?: string[];
  } | null;
  empty?: { title: React.ReactNode; body?: React.ReactNode; action?: React.ReactNode };
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  caption?: React.ReactNode;
  /** Stable initial sort. */
  initialSort?: { id: string; direction: 'asc' | 'desc' };
}

type SortState = { id: string; direction: 'asc' | 'desc' } | null;

const SKELETON_ROWS = 6;

export function DataTable<T>({
  data,
  columns,
  rowKey,
  loading,
  error,
  empty,
  onRowClick,
  rowClassName,
  caption,
  initialSort,
}: DataTableProps<T>) {
  const { t } = useTranslation('common');
  const [sort, setSort] = React.useState<SortState>(initialSort ?? null);
  const [hidden, setHidden] = React.useState<Set<string>>(
    () => new Set(columns.filter((c) => c.hiddenInitially).map((c) => c.id))
  );

  const visibleColumns = React.useMemo(
    () => columns.filter((c) => !hidden.has(c.id)),
    [columns, hidden]
  );

  const sortedData = React.useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortBy) return data;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const sortBy = col.sortBy;
    return [...data].sort((a, b) => {
      const av = sortBy(a);
      const bv = sortBy(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * dir;
      }
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [columns, data, sort]);

  function toggleSort(colId: string) {
    setSort((curr) => {
      if (curr?.id !== colId) return { id: colId, direction: 'asc' };
      if (curr.direction === 'asc') return { id: colId, direction: 'desc' };
      return null;
    });
  }

  function toggleColumn(colId: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  }

  if (error) {
    const isPending = error.code === 'not_implemented';
    return (
      <EmptyState
        variant={isPending ? 'pending' : 'error'}
        title={isPending ? t('table.notImplementedTitle') : t('table.errorTitle')}
        body={error.recovery ?? error.message}
        action={error.details?.length ? <ErrorDetails lines={error.details} /> : undefined}
      />
    );
  }
  if (!loading && data.length === 0) {
    return (
      <EmptyState
        title={empty?.title ?? t('table.emptyTitle')}
        body={empty?.body}
        action={empty?.action}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <details className="group">
          <summary className="cursor-pointer rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-1.5 text-xs text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-surface-2)]">
            {t('table.columns')}
          </summary>
          <div className="absolute end-6 z-20 mt-1 rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-2 shadow-lg">
            <ul className="space-y-1">
              {columns.map((c) => (
                <li key={c.id}>
                  <label className="flex items-center gap-2 px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={!hidden.has(c.id)}
                      onChange={() => toggleColumn(c.id)}
                      aria-label={typeof c.header === 'string' ? c.header : c.id}
                    />
                    <span>{c.header}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </details>
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-sm">
        <table className="w-full border-collapse text-sm">
          {caption ? (
            <caption className="px-4 py-3 text-start text-xs text-[color:var(--color-fg-muted)]">
              {caption}
            </caption>
          ) : null}
          <thead className="sticky top-0 z-10 bg-[color:var(--color-surface-2)]">
            <tr>
              {visibleColumns.map((col) => {
                const sorted = sort?.id === col.id ? sort.direction : undefined;
                const align =
                  col.align === 'end' ? 'text-end' : col.align === 'center' ? 'text-center' : 'text-start';
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={
                      sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'
                    }
                    className={cn(
                      'border-b border-[color:var(--color-border)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]',
                      align,
                      col.className
                    )}
                  >
                    {col.sortBy ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.id)}
                        className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-1 hover:text-[color:var(--color-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
                      >
                        <span>{col.header}</span>
                        {sorted === 'asc' ? (
                          <ChevronUp className="size-3.5" aria-hidden />
                        ) : sorted === 'desc' ? (
                          <ChevronDown className="size-3.5" aria-hidden />
                        ) : (
                          <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <tr key={`s-${i}`} aria-hidden>
                    {visibleColumns.map((col) => (
                      <td
                        key={col.id}
                        className="border-b border-[color:var(--color-border)] px-4 py-3"
                      >
                        <span className="block h-3 w-3/4 animate-pulse rounded bg-[color:var(--color-surface-3)]" />
                      </td>
                    ))}
                  </tr>
                ))
              : sortedData.map((row) => (
                  <tr
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'transition-colors hover:bg-[color:var(--color-surface-2)]/60',
                      onRowClick && 'cursor-pointer',
                      rowClassName?.(row)
                    )}
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          'border-b border-[color:var(--color-border)]/50 px-4 py-3 align-middle',
                          col.align === 'end'
                            ? 'text-end'
                            : col.align === 'center'
                              ? 'text-center'
                              : 'text-start',
                          col.className
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The diagnostic behind an error, collapsed. Left-to-right and monospaced in
 * every locale: these are schema paths an operator pastes into a bug report,
 * not prose.
 */
function ErrorDetails({ lines }: { lines: readonly string[] }) {
  const { t } = useTranslation('common');
  return (
    <details className="max-w-full text-start">
      <summary className="cursor-pointer text-xs font-semibold text-[color:var(--color-fg-muted)]">
        {t('table.errorDetails')}
      </summary>
      <ul
        dir="ltr"
        className="mt-2 space-y-1 break-all rounded-[var(--radius-md)] bg-[color:var(--color-surface)] p-2 text-start font-mono text-[11px] text-[color:var(--color-fg-muted)]"
      >
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </details>
  );
}
